import React from "react";
import { wallClockQhnum } from "../tasks_hub/taskHubUtils";
import { get_t_str } from "../../../../helpers/helpers";
import Tooltip from "@mui/material/Tooltip";
import TaskNameCardEmployeeModal from "./TaskNameCardEmployeeModal";
import { Lightbox } from "react-modal-image";
import TaskStatusBadge from "./TaskStatusBadge";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Employee-facing occurrence card. Leads with status (the badge; intraday
// rows show slot dots instead), says
// plainly whether the task is pinned to this person or shared with the shift,
// and shows checklist progress at a glance. Clicking opens the completion
// modal; intraday tasks expand into their time slots.

const DOT_CLASSES = {
  completed: "bg-emerald-500",
  completed_late: "bg-emerald-500",
  in_progress: "bg-amber-500",
  overdue: "bg-rose-500",
  not_started: "border-2 border-slate-300",
};

function AudienceChip({ task }) {
  const members = task?.plan_task_assignment_members || [];
  if (task?.is_pinned) {
    const others = Math.max(0, members.length - 1);
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 bg-violet-50 rounded-full px-2 py-0.5 whitespace-nowrap">
        Assigned to you{others > 0 ? ` +${others}` : ""}
      </span>
    );
  }
  return (
    <Tooltip title="Anyone on shift can complete this. It only needs to be done once.">
      <span className="inline-flex items-center text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 whitespace-nowrap">
        Team task
      </span>
    </Tooltip>
  );
}

function TaskNameCardEmployee(props) {
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedSlotTask, setSelectedSlotTask] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const handleOpenModal = (task) => {
    setSelectedSlotTask(task || props.task);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSlotTask(null);
  };

  const [showLighbox, setShowLightbox] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);

  const planTask = props.task?.plan_task;
  const isIntraday = props.intradaySlots && props.intradaySlots.length > 0;

  // Progress: only required subtasks count.
  const subtaskAssignments = props.task?.plan_subtask_assignments || [];
  const requiredSubtasks = subtaskAssignments.filter(
    (s) => !s.plan_subtask?.is_optional && s.plan_subtask?.type !== "heading"
  );
  const completedCount = requiredSubtasks.filter((s) => s.is_completed).length;
  const totalSubtasks = requiredSubtasks.length;
  const pct = totalSubtasks ? Math.round((completedCount / totalSubtasks) * 100) : 0;
  const isDone =
    props.task?.status === "completed" || props.task?.status === "completed_late";
  const activeModalTask = selectedSlotTask || props.task;

  const windowLabel =
    planTask?.lower_bound_qhnum != null &&
    planTask?.upper_bound_qhnum != null &&
    // Legacy rows use a 0-0 window to mean "no window"; formatting it would
    // claim the task runs midnight to midnight.
    !(planTask.lower_bound_qhnum === 0 && planTask.upper_bound_qhnum === 0)
      ? `${get_t_str(planTask.lower_bound_qhnum)} - ${get_t_str(planTask.upper_bound_qhnum)}`
      : "Anytime";

  return (
    <>
      {showLighbox && (
        <div className="absolute w-screen h-screen z-[9999]">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
      <div
        className="flex items-stretch gap-3 px-3 py-2.5 border border-slate-200 hover:border-violet-300 rounded-xl bg-white cursor-pointer transition-colors shadow-sm"
        onClick={() =>
          isIntraday ? setExpanded(!expanded) : handleOpenModal(props.task)
        }
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {planTask?.name}
            </p>
            <AudienceChip task={props.task} />
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-xs text-slate-500 tabular-nums">
              {windowLabel}
              {isIntraday && (
                <span className="text-violet-600 font-medium">
                  {" · every "}
                  {planTask?.intraday_repeat_interval_minutes >= 60
                    ? `${planTask?.intraday_repeat_interval_minutes / 60} hr`
                    : `${planTask?.intraday_repeat_interval_minutes} min`}
                </span>
              )}
            </p>
            {isIntraday ? (
              <span className="inline-flex items-center gap-1">
                {props.intradaySlots.map((slot, i) => (
                  <span
                    key={i}
                    className={`w-2 h-2 rounded ${
                      DOT_CLASSES[slot.status] || DOT_CLASSES.not_started
                    }`}
                  />
                ))}
                <span className="text-[11px] text-slate-400 ml-1 tabular-nums">
                  {props.intradaySlotsCompleted}/{props.intradayTotalSlots} done
                </span>
              </span>
            ) : (
              totalSubtasks > 0 &&
              !isDone && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-16 h-1.5 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
                    <span
                      className={`block h-full rounded-full ${
                        props.task?.status === "overdue"
                          ? "bg-rose-500"
                          : "bg-amber-500"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="text-[11px] text-slate-400 tabular-nums">
                    {completedCount}/{totalSubtasks}
                  </span>
                </span>
              )
            )}
          </div>
        </div>
        <div className="flex items-center flex-none">
          {isIntraday ? (
            expanded ? (
              <ExpandLessIcon className="text-slate-400" style={{ fontSize: "20px" }} />
            ) : (
              <ExpandMoreIcon className="text-slate-400" style={{ fontSize: "20px" }} />
            )
          ) : (
            <TaskStatusBadge
              status={props.task?.status}
              lifecycleStatus={props.task?.lifecycle_status}
              isOverdue={props.task?.is_overdue}
              isLate={props.task?.is_late}
              paused={props.task?.paused}
              submitted_at={props.task?.submitted_at_formatted}
              submittedBy={
                (props.task?.plan_task_assignment_members || []).find(
                  (m) => m.submitted_at
                )?.store_team_member?.full_name
              }
            />
          )}
        </div>
      </div>

      {/* Intraday expanded slot list */}
      {isIntraday && expanded && (
        <div className="ml-4 mt-1.5 space-y-1.5">
          {props.intradaySlots.map((slot, idx) => {
            const slotPlanTask = slot.plan_task;
            const intervalQhnum =
              (slotPlanTask?.intraday_repeat_interval_minutes || 60) / 60;
            // The occurrence's OWN start time (store-local, read off the
            // timestamp the backend wrote in the store's timezone), so a slot left behind by an
            // interval change keeps the hour it was recorded under. The index
            // arithmetic is the fallback for a row that predates available_at.
            const slotOpensAt = wallClockQhnum(slot.available_at);
            const slotStart = slotOpensAt != null
              ? slotOpensAt
              : (slotPlanTask?.lower_bound_qhnum || 0) +
                (slot.intraday_slot_index || 0) * intervalQhnum;
            const slotEnd = slotStart + intervalQhnum;
            const slotSubtasks = (slot.plan_subtask_assignments || []).filter(
              (s) =>
                !s.plan_subtask?.is_optional && s.plan_subtask?.type !== "heading"
            );
            const slotCompleted = slotSubtasks.filter((s) => s.is_completed).length;

            return (
              <div
                key={idx}
                className="flex items-center justify-between px-3 py-2 border border-slate-200 hover:border-violet-300 rounded-lg bg-white cursor-pointer transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(slot);
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded flex-none ${
                      DOT_CLASSES[slot.status] || DOT_CLASSES.not_started
                    }`}
                  />
                  <p className="text-xs font-medium text-slate-700 tabular-nums">
                    {get_t_str(slotStart)} - {get_t_str(slotEnd)}
                  </p>
                  {slotSubtasks.length > 0 && (
                    <p className="text-[11px] text-slate-400 tabular-nums">
                      {slotCompleted}/{slotSubtasks.length}
                    </p>
                  )}
                </div>
                <TaskStatusBadge
                  status={slot.status}
                  lifecycleStatus={slot.lifecycle_status}
                  isOverdue={slot.is_overdue}
                  isLate={slot.is_late}
                  paused={slot.paused}
                  submitted_at={slot.submitted_at_formatted}
                  submittedBy={
                    (slot.plan_task_assignment_members || []).find(
                      (m) => m.submitted_at
                    )?.store_team_member?.full_name
                  }
                />
              </div>
            );
          })}
        </div>
      )}

      <TaskNameCardEmployeeModal
        open={openModal}
        handleClose={handleCloseModal}
        task={activeModalTask}
        isAssignedToEveryone={activeModalTask?.is_assigned_to_everyone}
        save_plan_task_assignment={props.save_plan_task_assignment}
        onCommented={props.refreshOccurrences}
        homeCompany={props.homeCompany}
        setShowLightbox={setShowLightbox}
        setSelectedImage={setSelectedImage}
        roleNames={props.roleNames}
      />
    </>
  );
}

export default TaskNameCardEmployee;
